import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { LOADING_STATUS } from '../../common/constants';
import tagsService from '../../common/api/tagsService';
import { RootState } from '../../app/store';

export interface Tag {
  id: string;
  name: string;
  siteId: string;
}

interface TagState {
  tagItems: Tag[];
  priceGroupTags: Tag[];
  tagDetails: Tag | null;
  status: LOADING_STATUS.IDLE | LOADING_STATUS.LOADING | LOADING_STATUS.FAILED;
}

const initialState: TagState = {
  tagItems: [],
  priceGroupTags: [],
  tagDetails: null,
  status: LOADING_STATUS.IDLE,
};

export const initialTag: Tag = {
  id: '',
  name: '',
  siteId: '',
};

export const getPriceGroupTags = createAsyncThunk(
  'tags/getPriceGroupTags',
  async (_, { rejectWithValue }) => {
    try {
      return await tagsService.fetchTagsByType(1);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAccommodationTags = createAsyncThunk(
  'tags/getAccommodationTags',
  async (_, { rejectWithValue }) => {
    try {
      return await tagsService.fetchAccommodationTags();
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getTagDetails = createAsyncThunk(
  'tags/getTagDetails',
  async (tagId: string, { rejectWithValue }) => {
    try {
      return await tagsService.fetchTagDetails(tagId);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const saveOrUpdateTag = createAsyncThunk(
  'tags/saveOrUpdateTag',
  async (tagData: Tag, { rejectWithValue }) => {
    try {
      return await tagsService.saveOrUpdateTag(tagData);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

const tagsSlice = createSlice({
  name: 'tags',
  initialState,
  reducers: {
    updateTagDetails: (state, action: PayloadAction<Tag>) => {
      state.tagDetails = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getPriceGroupTags.fulfilled, (state, action) => {
        state.priceGroupTags = action.payload;
      })
      .addCase(getAccommodationTags.pending, state => {
        state.status = LOADING_STATUS.LOADING;
      })
      .addCase(getAccommodationTags.fulfilled, (state, action) => {
        state.status = LOADING_STATUS.IDLE;
        state.tagItems = action.payload;
      })
      .addCase(getAccommodationTags.rejected, (state, action) => {
        state.status = LOADING_STATUS.FAILED;
      })
      .addCase(getTagDetails.pending, state => {
        state.status = LOADING_STATUS.LOADING;
      })
      .addCase(getTagDetails.fulfilled, (state, action) => {
        state.status = LOADING_STATUS.IDLE;
        state.tagDetails = action.payload;
      })
      .addCase(getTagDetails.rejected, state => {
        state.status = LOADING_STATUS.FAILED;
      });
  },
});

export const { updateTagDetails } = tagsSlice.actions;

export const selectTagItems = (state: RootState) =>
  state.accommodationTags.tagItems;

export const selectPriceGroupTags = (state: RootState) =>
  state.accommodationTags.priceGroupTags;

export const selectTagDetails = (state: RootState) =>
  state.accommodationTags.tagDetails;

export const selectTagDataTableItems = createSelector(
  selectTagItems,
  tagItems =>
    tagItems.map(item => ({
      ItemId: item.id,
      Items: [item.name],
      Url: `/accommodations/tags/${item.id}`,
    })),
);

export default tagsSlice.reducer;
